import { useNavigate } from "react-router-dom";
import { Heart, Star } from "lucide-react";
import { formatINR } from "@/constants/branding";
import { useStore } from "@/context/StoreContext";

export default function ProductCard({ product }) {
  const navigate = useNavigate();
  const { addToCart, toggleWishlist, isWished } = useStore();
  const wished = isWished(product.id);
  const off = product.mrp && product.mrp > product.price
    ? Math.round(((product.mrp - product.price) / product.mrp) * 100)
    : 0;
  const outOfStock = product.stock <= 0;
  const go = () => navigate(`/products/${product.id}`);

  return (
    <div
      className="group relative flex flex-col h-full bg-card rounded-2xl border border-border overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_-20px_rgba(6,48,28,0.35)] hover:border-primary/30"
      data-testid={`product-card-${product.id}`}
    >
      <div className="relative aspect-[4/5] bg-secondary overflow-hidden cursor-pointer" onClick={go}>
        <img
          src={(product.images || [])[0]}
          alt={product.name}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-contain p-4 mix-blend-multiply transition-transform duration-500 group-hover:scale-[1.04]"
        />
        {off > 0 && (
          <span className="absolute top-3 left-3 bg-gold text-deep text-[11px] font-extrabold tracking-wide px-2.5 py-1 rounded-full shadow-sm">
            {off}% OFF
          </span>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); toggleWishlist(product.id); }}
          className="absolute top-3 right-3 h-9 w-9 rounded-full bg-white/95 flex items-center justify-center shadow-md hover:scale-110 transition-transform"
          data-testid={`product-wishlist-${product.id}`}
          aria-label="Toggle wishlist"
        >
          <Heart className={`h-[18px] w-[18px] ${wished ? "fill-rose-500 text-rose-500" : "text-deep/60"}`} />
        </button>
        {outOfStock && (
          <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
            <span className="text-sm font-semibold text-deep bg-white px-4 py-1.5 rounded-full shadow">Out of stock</span>
          </div>
        )}
      </div>

      <div className="p-4 flex flex-col flex-1">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground truncate">{product.vendor_name || product.category}</p>
        <h3
          className="mt-1 text-sm font-semibold leading-snug text-foreground line-clamp-2 min-h-[2.6rem] cursor-pointer group-hover:text-primary transition-colors"
          onClick={go}
        >
          {product.name}
        </h3>

        <div className="mt-2 h-5">
          {product.rating ? (
            <span className={`rating-chip ${Number(product.rating) < 3 ? "mid" : ""}`}>
              <Star className="h-3 w-3 fill-current" /> {product.rating}
            </span>
          ) : null}
        </div>

        <div className="mt-2 flex items-baseline flex-wrap gap-x-2">
          <span className="text-lg font-extrabold text-foreground">{formatINR(product.price)}</span>
          {off > 0 && <span className="text-xs text-muted-foreground line-through">{formatINR(product.mrp)}</span>}
        </div>

        <div className="mt-auto pt-4">
          <button
            disabled={outOfStock}
            onClick={() => addToCart(product.id, 1)}
            className="flex h-10 w-full items-center justify-center rounded-xl border-[1.5px] border-primary text-primary text-sm font-bold transition-colors hover:bg-primary hover:text-white disabled:cursor-not-allowed disabled:border-border disabled:text-muted-foreground disabled:hover:bg-transparent disabled:hover:text-muted-foreground"
            data-testid={`product-add-to-cart-${product.id}`}
          >
            {outOfStock ? "Unavailable" : "Add to Cart"}
          </button>
        </div>
      </div>
    </div>
  );
}
